import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getContactInfo, updateContactInfo, ContactInfo } from '../services/adminApi';

const ContactInfoPage = () => {
  const { t } = useTranslation();
  const [model, setModel] = useState<ContactInfo>({ socialLinks: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getContactInfo()
      .then((data) => setModel((current) => ({ ...current, ...data })))
      .catch((err) => setError(err instanceof Error ? err.message : t('contactInfo.loadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  const onChange = (key: keyof ContactInfo, value: string) => {
    setModel((current) => ({ ...current, [key]: value } as ContactInfo));
  };

  const onSocialChange = (key: keyof NonNullable<ContactInfo['socialLinks']>, value: string) => {
    setModel((current) => ({ ...current, socialLinks: { ...(current.socialLinks || {}), [key]: value } }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await updateContactInfo(model);
      setSaved(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('contactInfo.saveFailed'));
    }
  };

  if (loading) return <div>{t('contactInfo.loading')}</div>;

  return (
    <div className="page-grid">
      <div className="section-heading">
        <h2>{t('contactInfo.title')}</h2>
        <p>{t('contactInfo.description')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {saved && <div className="alert alert-success">{t('contactInfo.savedMessage')}</div>}

      <form className="form-grid" onSubmit={handleSave}>
        <div className="form-group">
          <label>{t('contactInfo.address')}</label>
          <input className="form-control" value={model.address ?? ''} onChange={(e) => onChange('address', e.target.value)} />
        </div>

        <div className="form-group">
          <label>{t('contactInfo.email')}</label>
          <input className="form-control" value={model.email ?? ''} onChange={(e) => onChange('email', e.target.value)} />
        </div>

        <div className="form-group">
          <label>{t('contactInfo.phone')}</label>
          <input className="form-control" value={model.phone ?? ''} onChange={(e) => onChange('phone', e.target.value)} />
        </div>

        <fieldset style={{ border: '1px solid #e5e7eb', padding: '1rem', borderRadius: 8 }}>
          <legend>{t('contactInfo.socialLinksLegend')}</legend>
          <div className="form-group">
            <label>{t('contactInfo.facebook')}</label>
            <input className="form-control" value={model.socialLinks?.facebook ?? ''} onChange={(e) => onSocialChange('facebook', e.target.value)} />
          </div>
          <div className="form-group">
            <label>{t('contactInfo.twitter')}</label>
            <input className="form-control" value={model.socialLinks?.twitter ?? ''} onChange={(e) => onSocialChange('twitter', e.target.value)} />
          </div>
          <div className="form-group">
            <label>{t('contactInfo.linkedin')}</label>
            <input className="form-control" value={model.socialLinks?.linkedin ?? ''} onChange={(e) => onSocialChange('linkedin', e.target.value)} />
          </div>
          <div className="form-group">
            <label>{t('contactInfo.instagram')}</label>
            <input className="form-control" value={model.socialLinks?.instagram ?? ''} onChange={(e) => onSocialChange('instagram', e.target.value)} />
          </div>
        </fieldset>

        <div className="form-group">
          <label>{t('contactInfo.mapEmbedUrl')}</label>
          <input className="form-control" value={model.mapEmbedUrl ?? ''} onChange={(e) => onChange('mapEmbedUrl', e.target.value)} />
        </div>

        <div className="form-actions" style={{ gridColumn: '1 / -1' }}>
          <button className="btn btn-primary" type="submit">{t('contactInfo.save')}</button>
        </div>
      </form>
    </div>
  );
};

export default ContactInfoPage;
